import React, { useCallback, useEffect, useState } from 'react';
import { ImagePlus, Trash2 } from 'lucide-react';
import {
  MEMBERSHIP_COVER_DESKTOP_MEDIA_KEY,
  MEMBERSHIP_COVER_MEDIA_KEY,
  fetchMembershipGstSettings,
  updateMembershipGstSettings,
} from '../../../services/MasterAdmin/membershipService';
import {
  deleteMediaAsset,
  fetchMediaAssets,
  fetchPublicMediaByKey,
  uploadMediaAsset,
} from '../../../services/MasterAdmin/mediaAssetService';
import MembershipPanelShell from './MembershipPanelShell';
import { MembershipError, MembershipLoading, MembershipSuccess } from './MembershipAlerts';
import { msInput, msPrimaryBtn, msSecondaryBtn } from './membershipUi';

type CoverSlot = {
  key: string;
  url: string | null;
  assetId: number | null;
};

const MembershipSettingsPanel: React.FC = () => {
  const [mobileCover, setMobileCover] = useState<CoverSlot>({
    key: MEMBERSHIP_COVER_MEDIA_KEY,
    url: null,
    assetId: null,
  });
  const [desktopCover, setDesktopCover] = useState<CoverSlot>({
    key: MEMBERSHIP_COVER_DESKTOP_MEDIA_KEY,
    url: null,
    assetId: null,
  });
  const [gstPercent, setGstPercent] = useState(0);
  const [gstMode, setGstMode] = useState<'inclusive' | 'exclusive'>('exclusive');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadOneCover = async (mediaKey: string): Promise<CoverSlot> => {
    const listed = await fetchMediaAssets({ key: mediaKey, limit: 1 });
    const asset = listed.data?.[0] || null;
    let url: string | null = null;
    try {
      const pub = await fetchPublicMediaByKey(mediaKey);
      url = pub.fileUrl || null;
    } catch {
      url = null;
    }
    return { key: mediaKey, url, assetId: asset?.id || null };
  };

  const loadCovers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [mobile, desktop] = await Promise.all([
        loadOneCover(MEMBERSHIP_COVER_MEDIA_KEY),
        loadOneCover(MEMBERSHIP_COVER_DESKTOP_MEDIA_KEY),
      ]);
      setMobileCover(mobile);
      setDesktopCover(desktop);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load cover images');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadGst = useCallback(async () => {
    const settings = await fetchMembershipGstSettings();
    setGstPercent(Number(settings.gstPercent || 0));
    setGstMode(settings.gstMode === 'inclusive' ? 'inclusive' : 'exclusive');
  }, []);

  useEffect(() => {
    void Promise.all([loadCovers(), loadGst()]).catch((e: any) => {
      setError(e?.response?.data?.message || e?.message || 'Failed to load settings');
    });
  }, [loadCovers, loadGst]);

  const uploadCover = async (
    file: File,
    mediaKey: string,
    label: string,
    setSlot: React.Dispatch<React.SetStateAction<CoverSlot>>
  ) => {
    try {
      setBusy(true);
      setError(null);
      await uploadMediaAsset({
        file,
        key: mediaKey,
        name: file.name || label,
      });
      setSuccess(`${label} saved.`);
      const next = await loadOneCover(mediaKey);
      setSlot(next);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || `Failed to upload ${label.toLowerCase()}`);
    } finally {
      setBusy(false);
    }
  };

  const removeCover = async (
    slot: CoverSlot,
    label: string,
    setSlot: React.Dispatch<React.SetStateAction<CoverSlot>>
  ) => {
    if (!slot.assetId) {
      setSlot({ ...slot, url: null, assetId: null });
      return;
    }
    try {
      setBusy(true);
      setError(null);
      await deleteMediaAsset(slot.assetId);
      setSlot({ ...slot, url: null, assetId: null });
      setSuccess(`${label} deleted.`);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || `Failed to delete ${label.toLowerCase()}`);
    } finally {
      setBusy(false);
    }
  };

  const renderCoverSection = (opts: {
    title: string;
    description: React.ReactNode;
    mediaKey: string;
    slot: CoverSlot;
    setSlot: React.Dispatch<React.SetStateAction<CoverSlot>>;
    label: string;
    previewClassName: string;
    emptyHint: string;
  }) => (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{opts.title}</h3>
      <p className="mt-1 text-sm text-gray-500">
        {opts.description} Key:{' '}
        <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-800">{opts.mediaKey}</code>
      </p>

      <div
        className={`mt-4 overflow-hidden rounded-xl border border-dashed border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/40 ${opts.previewClassName}`}
      >
        {opts.slot.url ? (
          <img
            src={opts.slot.url}
            alt={opts.label}
            className="h-full w-full object-contain"
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400">
            <ImagePlus className="h-8 w-8" />
            <span className="text-sm">{opts.emptyHint}</span>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <label className={`${msPrimaryBtn} cursor-pointer ${busy ? 'opacity-50' : ''}`}>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void uploadCover(file, opts.mediaKey, opts.label, opts.setSlot);
              e.target.value = '';
            }}
          />
          {opts.slot.url ? `Replace ${opts.label.toLowerCase()}` : `Upload ${opts.label.toLowerCase()}`}
        </label>
        {opts.slot.url ? (
          <button
            type="button"
            disabled={busy}
            className={`${msSecondaryBtn} text-red-600`}
            onClick={() => void removeCover(opts.slot, opts.label, opts.setSlot)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </button>
        ) : null}
      </div>
    </section>
  );

  return (
    <MembershipPanelShell title="Settings">
      <MembershipError message={error} />
      <MembershipSuccess message={success} />
      {loading ? <MembershipLoading /> : null}

      <div className="max-w-3xl space-y-4">
        <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">GST</h3>
          <p className="mt-1 text-sm text-gray-500">
            Leave GST at 0 to hide it. Set 1% or more to show GST on app and web. This value is saved on all membership plans.
          </p>
          <div className="mt-4 grid gap-3">
            <label className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-200">
              GST %
              <input
                type="number"
                min={0}
                step="0.01"
                value={gstPercent}
                onChange={(e) => setGstPercent(Number(e.target.value))}
                className={msInput}
              />
            </label>
            <div className="text-sm text-gray-700 dark:text-gray-200">
              GST type
              <div className="mt-2 flex flex-wrap gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="settings-gst-mode"
                    checked={gstMode === 'exclusive'}
                    onChange={() => setGstMode('exclusive')}
                  />
                  Exclusive (add GST on top of plan price)
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="settings-gst-mode"
                    checked={gstMode === 'inclusive'}
                    onChange={() => setGstMode('inclusive')}
                  />
                  Inclusive (GST already in plan price)
                </label>
              </div>
            </div>
            <button
              type="button"
              disabled={busy}
              className={`${msPrimaryBtn} w-fit`}
              onClick={async () => {
                try {
                  setBusy(true);
                  setError(null);
                  const result = await updateMembershipGstSettings({
                    gstPercent: Math.max(0, Number(gstPercent) || 0),
                    gstMode,
                  });
                  setSuccess(result?.message || 'GST settings saved on all plans.');
                } catch (e: any) {
                  setError(e?.response?.data?.message || 'Failed to save GST settings');
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save GST
            </button>
          </div>
        </section>

        {renderCoverSection({
          title: 'App / mobile cover image',
          description: (
            <>
              Used on the membership home card in the app, mobile web, and sidebars.
            </>
          ),
          mediaKey: MEMBERSHIP_COVER_MEDIA_KEY,
          slot: mobileCover,
          setSlot: setMobileCover,
          label: 'App cover',
          previewClassName: 'h-56',
          emptyHint: 'No cover image uploaded',
        })}

        {renderCoverSection({
          title: 'Web desktop banner image',
          description: (
            <>
              Wide strip (full width, short height) shown only on the web desktop home. Recommended
              aspect ~5:1 or wider.
            </>
          ),
          mediaKey: MEMBERSHIP_COVER_DESKTOP_MEDIA_KEY,
          slot: desktopCover,
          setSlot: setDesktopCover,
          label: 'Desktop banner',
          previewClassName: 'h-28',
          emptyHint: 'No desktop banner uploaded',
        })}
      </div>
    </MembershipPanelShell>
  );
};

export default MembershipSettingsPanel;
